import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Copy, Check, Info } from 'lucide-react';
import Button from '../Button';

interface SuccessScreenProps {
  referenceNumber: string;
  isDuplicate: boolean;
}

export default function SuccessScreen({
  referenceNumber,
  isDuplicate,
}: SuccessScreenProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referenceNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="rounded-3xl border-2 border-gray-200 bg-white p-6 sm:p-10 text-center shadow-sm max-w-xl mx-auto space-y-6 animate-scale-in">
      {/* Icon Badge */}
      <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
        <CheckCircle2 className="w-9 h-9 sm:w-11 sm:h-11" />
      </div>

      {/* Heading & Subtitle */}
      <div className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C]">
          {isDuplicate ? 'إحنا مستلمين طلبك بالفعل' : 'تم استلام طلبك بنجاح'}
        </h2>
        <p className="text-base font-semibold text-[#5B3FD0]">
          شكرًا لتقديمك في منحة EduNasser.
        </p>
      </div>

      {/* Duplicate Notice or Standard Message */}
      {isDuplicate && (
        <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-amber-200 bg-amber-50/70 text-xs sm:text-sm text-amber-900 text-start">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="font-medium">
            لقينا طلب متقدم قبل كده بنفس بيانات التواصل. ده رقم طلبك.
          </p>
        </div>
      )}

      {/* Next Steps Message */}
      <p className="text-sm text-[#707070] leading-relaxed max-w-md mx-auto">
        فريقنا هيراجع بياناتك والمستندات، ولو تم اختيارك للمرحلة التالية هنتواصل معاك عن طريق رقم الموبايل أو البريد الإلكتروني اللي سجلتهم.
      </p>

      {/* Reference Number Card */}
      <div className="rounded-2xl border-2 border-[#5B3FD0]/20 bg-[#5B3FD0]/5 p-5 text-center space-y-3">
        <p className="text-xs sm:text-sm font-semibold text-[#707070]">
          رقم طلبك
        </p>
        <div className="flex items-center justify-center gap-3">
          <span
            dir="ltr"
            className="font-mono text-xl sm:text-2xl font-bold tracking-wider text-[#5B3FD0] bg-white px-4 py-2 rounded-xl border border-[#5B3FD0]/20 shadow-xs"
          >
            {referenceNumber}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold text-[#5B3FD0] bg-white hover:bg-gray-50 border border-[#5B3FD0]/20 transition-all active:scale-95 shadow-xs"
            aria-label="نسخ رقم الطلب"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-600">تم النسخ</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>نسخ</span>
              </>
            )}
          </button>
        </div>
        <p className="text-[11px] text-[#707070]">
          احتفظ برقم الطلب، هتحتاجه في أي تواصل مع الفريق.
        </p>
      </div>

      {/* Return to home button */}
      <div className="pt-2">
        <Link to="/">
          <Button variant="secondary" className="w-full sm:w-auto px-8 py-3 text-sm font-semibold">
            ارجع للرئيسية
          </Button>
        </Link>
      </div>
    </div>
  );
}
